import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CAMPAIGN } from "@/constants/testIds";
import { ROLES, SIZES, SITE_TYPES, PILOT_SITE, TIMEFRAMES, RECORDS, ISO_POS, track } from "@/constants/campaignDefs";
import { Leaf, CheckCircle2, Loader2 } from "lucide-react";
import { RootsMark } from "@/components/RootsMark";
import { toast } from "sonner";

const LIME = "#B7E64B";
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-[#09292D] focus:border-[#09292D] focus:outline-none focus:ring-2 focus:ring-[#B7E64B]";

export default function CampaignProfilePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [p, setP] = useState({
    role: "", company_size: "", customer_types: [], pilot_site: "",
    start_timeframe: "", records_available: [], iso14001: "", anything_else: "", uen: "",
  });

  useEffect(() => {
    api.get(`/campaign/profile/${token}`)
      .then((r) => {
        setLead(r.data);
        if (r.data.profile) setP((prev) => ({ ...prev, ...r.data.profile }));
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const toggle = (field, val) =>
    setP((prev) => ({ ...prev, [field]: prev[field].includes(val) ? prev[field].filter((x) => x !== val) : [...prev[field], val] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/campaign/profile/${token}`, p);
      setSaved(true);
      track("profile_saved");
      toast.success("Thanks — your details are saved");
    } catch { toast.error("Could not save. Please try again."); }
    setSaving(false);
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#F5F7F2]" data-testid={CAMPAIGN.profile}>
      <header className="bg-[#09292D] text-white">
        <div className="mx-auto flex max-w-[1160px] items-center gap-2 px-5 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: LIME }}>
            <RootsMark className="h-5 w-5 text-[#09292D]" />
          </div>
          <p className="font-heading text-lg font-bold">LIAS<span style={{ color: LIME }}>ROOTS</span></p>
        </div>
      </header>
      <div className="mx-auto max-w-[680px] px-5 py-12">{children}</div>
    </div>
  );

  if (notFound) return <Shell><div className="rounded-2xl border border-slate-200 bg-white p-8 text-center"><p className="text-sm text-slate-600">This link is no longer valid.</p></div></Shell>;
  if (!lead) return <Shell><div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading…</div></Shell>;

  if (saved) return (
    <Shell>
      <div className="rounded-2xl border border-[#9ccc2f] bg-white p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-[#4e8a1f]" />
        <h1 className="mt-3 font-heading text-2xl font-bold text-[#09292D]">Thank you</h1>
        <p className="mt-2 text-sm text-slate-600">Your extra details are saved and will help our discovery conversation. Your reference <b>{lead.reference}</b> and queue position are unchanged.</p>
      </div>
    </Shell>
  );

  const Chip = ({ active, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${active ? "border-[#09292D] bg-[#09292D] text-white" : "border-slate-300 bg-white text-[#09292D]"}`}>{children}</button>
  );

  return (
    <Shell>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <h1 className="font-heading text-2xl font-bold text-[#09292D]">Help us prepare</h1>
        <p className="mt-1 text-sm text-slate-600">{lead.company} · reference {lead.reference}. Everything here is optional — skip anything you'd rather discuss. Please don't include client names, addresses or any sensitive personal information.</p>

        <div className="mt-6 space-y-6">
          <div>
            <p className="mb-2 text-sm font-semibold text-[#09292D]">Your role</p>
            <div className="flex flex-wrap gap-2">{ROLES.map((r) => <Chip key={r} active={p.role === r} onClick={() => setP({ ...p, role: p.role === r ? "" : r })}>{r}</Chip>)}</div>
          </div>
          <div>
            <p className="mb-2 text-sm font-semibold text-[#09292D]">Company size</p>
            <div className="flex flex-wrap gap-2">{SIZES.map((s) => <Chip key={s} active={p.company_size === s} onClick={() => setP({ ...p, company_size: p.company_size === s ? "" : s })}>{s}</Chip>)}</div>
          </div>
          <div>
            <p className="mb-2 text-sm font-semibold text-[#09292D]">Main customer / site types</p>
            <div className="flex flex-wrap gap-2">{SITE_TYPES.map((s) => <Chip key={s} active={p.customer_types.includes(s)} onClick={() => toggle("customer_types", s)}>{s}</Chip>)}</div>
          </div>
          <div>
            <p className="mb-2 text-sm font-semibold text-[#09292D]">A potential pilot site?</p>
            <div className="flex flex-wrap gap-2">{PILOT_SITE.map((s) => <Chip key={s.key} active={p.pilot_site === s.key} onClick={() => setP({ ...p, pilot_site: p.pilot_site === s.key ? "" : s.key })}>{s.label}</Chip>)}</div>
          </div>
          <div>
            <p className="mb-2 text-sm font-semibold text-[#09292D]">Desired start timeframe</p>
            <div className="flex flex-wrap gap-2">{TIMEFRAMES.map((s) => <Chip key={s} active={p.start_timeframe === s} onClick={() => setP({ ...p, start_timeframe: p.start_timeframe === s ? "" : s })}>{s}</Chip>)}</div>
          </div>
          <div>
            <p className="mb-2 text-sm font-semibold text-[#09292D]">Which records do you keep?</p>
            <div className="flex flex-wrap gap-2">{RECORDS.map((s) => <Chip key={s} active={p.records_available.includes(s)} onClick={() => toggle("records_available", s)}>{s}</Chip>)}</div>
          </div>
          <div>
            <p className="mb-2 text-sm font-semibold text-[#09292D]">ISO 14001 position</p>
            <div className="flex flex-wrap gap-2">{ISO_POS.map((s) => <Chip key={s} active={p.iso14001 === s} onClick={() => setP({ ...p, iso14001: p.iso14001 === s ? "" : s })}>{s}</Chip>)}</div>
          </div>
          <div>
            <label htmlFor="pr-uen" className="mb-1 block text-sm font-semibold text-[#09292D]">Company UEN <span className="font-normal text-slate-500">(optional)</span></label>
            <input id="pr-uen" className={inputCls} value={p.uen} onChange={(e) => setP({ ...p, uen: e.target.value })}
                   data-testid={CAMPAIGN.fieldUen} placeholder="e.g. 201812345A" />
            <p className="mt-1 text-xs text-slate-500">Helps us link colleagues from the same company.</p>
          </div>
          <div>
            <label htmlFor="pr-else" className="mb-1 block text-sm font-semibold text-[#09292D]">Anything else?</label>
            <textarea id="pr-else" rows={3} className={inputCls} value={p.anything_else} onChange={(e) => setP({ ...p, anything_else: e.target.value })}
                      placeholder="Optional. Please don't include client names, addresses or confidential information." />
          </div>

          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid={CAMPAIGN.profileSave}
                    className="flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-[#09292D] disabled:opacity-60" style={{ backgroundColor: LIME }}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save details
            </button>
            <button onClick={() => { track("profile_skipped"); navigate("/liasroots"); }} data-testid={CAMPAIGN.profileSkip}
                    className="rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-[#09292D]">
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </Shell>
  );
}
